import { useCallback } from 'react';
import {
	ConnectionHandler,
	ConnectionInterface,
	type RecordProxy,
	type RecordSourceProxy,
} from 'relay-runtime';
import RelayDataID from '@atlassian/relay-data-id';
import {
	useConnectionsList,
	type ConnectionDetails,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/connections-list/index.tsx';
import type { realtimeUpdaterOld_issueCreated_Subscription$data as IssueCreateSubscription } from '@atlassian/jira-relay/src/__generated__/realtimeUpdaterOld_issueCreated_Subscription.graphql.ts';
import type { SearchViewContextList } from '../../services/realtime-issue-details/types.tsx';
import {
	insertNewGroupInGroupsConnection,
	useUpdateConnectionDetails,
	updateParentHasChildren,
} from './utils.tsx';

export type SearchViewContextOld = NonNullable<
	NonNullable<IssueCreateSubscription['jira']>['onIssueCreatedByProject']
>['searchViewContext'];
export type SearchViewContextMappingOld = NonNullable<
	NonNullable<SearchViewContextOld>['contexts']
>[number];

export type GroupDetails = { __id: string; id: string; afterGroupId?: string | null };
export type GroupsForIssue = GroupDetails[];

export type UpdateIssuesOnConnectionArgs = {
	store: RecordSourceProxy;
	connection: ConnectionDetails;
	searchViewContexts: SearchViewContextList;
	updatedIssues: Map<
		string,
		{
			issueId: string;
			issueRecordId: string;
			fieldSetsRecordId: string | undefined;
		}
	>;
	groupConnectionId?: string;
	projectKey?: string;
};

export type UpdateNewParentsOnConnectionArgs = {
	store: RecordSourceProxy;
	connection: ConnectionDetails;
	updatedParents: Set<string>;
	projectKey?: string;
};

export type UpdateIssuesOnConnectionResult = {
	issuesUnmoved: Set<string>;
	issuesMoved: Set<string>;
	issuesRemoved: Set<string>;
	issuesUnrelated: Set<string>;
};

export type IssuePositionUpdaterArgs = {
	store: RecordSourceProxy;
	searchViewContext: SearchViewContextOld;
	issueId: string;
	issueRecordId: string;
	parentIssueNodeRecordId?: string;
	groupConnectionId?: string;
	fieldSetConnectionId: string | undefined;
	projectKey?: string;
};

export type IssueDeleteUpdaterArgs = {
	store: RecordSourceProxy;
	/** Resource ID of the issue to find and remove from the store. */
	issueResourceId: string;
	groupConnectionId?: string;
	projectKey?: string;
};

const JIRA_ISSUE_TYPENAME = 'JiraIssue';

const addEdgeToConnection = ({
	store,
	connectionRecord,
	fieldSetsRecordId,
	issueRecordId,
	position,
	projectKey,
}: {
	store: RecordSourceProxy;
	connectionRecord: RecordProxy<{}>;
	fieldSetsRecordId: string | undefined;
	issueRecordId: string;
	position: number | null | undefined;
	projectKey?: string;
}) => {
	const issueRecord = store.get(issueRecordId);
	const fieldSetsRecord = fieldSetsRecordId ? store.get(fieldSetsRecordId) : undefined;
	if (!issueRecord || !fieldSetsRecord) {
		return;
	}
	const edge = ConnectionHandler.createEdge(store, connectionRecord, issueRecord, 'JiraIssueEdge');
	const fieldSets = edge.getOrCreateLinkedRecord('fieldSets', 'JiraIssueFieldSetEdge', {
		first: 500,
	});
	fieldSets.copyFieldsFrom(fieldSetsRecord);

	const updateArgs = projectKey ? { projectKey } : undefined;
	const canHaveChildIssues = issueRecord.getValue('canHaveChildIssues', updateArgs);
	if (canHaveChildIssues) {
		edge.setValue(true, 'canHaveChildIssues', updateArgs);
	}

	// no before or after implies an empty conneciton, verify then create the edges list
	const { EDGES } = ConnectionInterface.get();
	const edges = connectionRecord.getLinkedRecords(EDGES);
	if (!edges || position === -1 || position == null) {
		return;
	}
	const nextEdges = edges.slice(0, position).concat(edge).concat(edges.slice(position));

	connectionRecord.setLinkedRecords(nextEdges, EDGES);
};

export const useIssueRealtimeUpdater = () => {
	const { connections } = useConnectionsList();
	const updateConnectionDetails = useUpdateConnectionDetails();

	const updateIssuesOnConnection = useCallback(
		({
			store,
			connection,
			searchViewContexts,
			updatedIssues,
			groupConnectionId,
			projectKey,
		}: UpdateIssuesOnConnectionArgs): UpdateIssuesOnConnectionResult => {
			const issuesProcessedAnalytics: UpdateIssuesOnConnectionResult = {
				issuesUnmoved: new Set<string>(),
				issuesMoved: new Set<string>(),
				issuesRemoved: new Set<string>(),
				issuesUnrelated: new Set<string>(),
			};
			const connectionRecord = store.get(connection.connectionId);
			if (!connectionRecord) {
				return issuesProcessedAnalytics;
			}

			const issuesWithContext = new Set(
				searchViewContexts?.flatMap((context) => context?.sourceIssueId).filter(Boolean),
			);

			const startingIssuesInConnection = new Set(
				connectionRecord
					.getLinkedRecords('edges')
					?.map((edge) => edge?.getLinkedRecord('node')?.getDataID()),
			);

			let changeInIssueCount = 0;

			updatedIssues.forEach((issue) => {
				if (!issuesWithContext.has(issue.issueId)) {
					if (startingIssuesInConnection.has(issue.issueRecordId)) {
						ConnectionHandler.deleteNode(connectionRecord, issue.issueRecordId);
						changeInIssueCount -= 1;

						issuesProcessedAnalytics.issuesRemoved.add(issue.issueId);
					} else {
						issuesProcessedAnalytics.issuesUnrelated.add(issue.issueId);
					}
				}
			});

			searchViewContexts?.forEach((context) => {
				const currentIssues = connectionRecord.getLinkedRecords('edges');
				const issueForContext = updatedIssues.get(context.sourceIssueId);
				if (!currentIssues || !issueForContext) {
					return;
				}
				const issueAtPosition = currentIssues[context.position];
				if (
					(issueAtPosition &&
						issueAtPosition.getLinkedRecord('node')?.getDataID() !==
							issueForContext.issueRecordId) ||
					context.position === currentIssues.length // the item would be the next loaded issue, insert in case the last page is loaded to ensure it appears in all cases
				) {
					if (startingIssuesInConnection.has(issueForContext.issueRecordId)) {
						ConnectionHandler.deleteNode(connectionRecord, issueForContext.issueRecordId);
					} else {
						changeInIssueCount += 1;
					}

					addEdgeToConnection({
						store,
						connectionRecord,
						fieldSetsRecordId: issueForContext.fieldSetsRecordId,
						issueRecordId: issueForContext.issueRecordId,
						position: context.position,
					});

					issuesProcessedAnalytics.issuesMoved.add(issueForContext.issueId);
				} else if (issueAtPosition != null) {
					issuesProcessedAnalytics.issuesUnmoved.add(issueForContext.issueId);
				} else {
					issuesProcessedAnalytics.issuesUnrelated.add(issueForContext.issueId);
				}
			});

			updateConnectionDetails({
				store,
				connection,
				operation: 'add', // This will never be consumed, but is required by the type
				netChangeInIssueCount: changeInIssueCount,
				remainingIssueCount: connectionRecord.getLinkedRecords('edges')?.length,
				groupConnectionId,
				projectKey,
			});

			return issuesProcessedAnalytics;
		},
		[updateConnectionDetails],
	);

	const updateNewParentsOnConnection = useCallback(
		({ store, connection, updatedParents, projectKey }: UpdateNewParentsOnConnectionArgs) => {
			const connectionRecord = store.get(connection.connectionId);
			const { EDGES } = ConnectionInterface.get();
			const edges = connectionRecord?.getLinkedRecords(EDGES);
			edges?.forEach((edge) => {
				const parentIssueRecordId = edge?.getLinkedRecord('node')?.getDataID();
				if (parentIssueRecordId && updatedParents.has(parentIssueRecordId)) {
					updateParentHasChildren({
						store,
						parentIssueEdgeRecordId: edge?.getDataID(),
						// this refers the the children connection of the parent, where this update is designed to work on parents where no connection can exist now
						connectionId: undefined,
						hasChildren: true,
						projectKey,
					});
				}
			});
		},
		[],
	);
	const issuePositionUpdater = useCallback(
		({
			store,
			searchViewContext,
			issueRecordId,
			parentIssueNodeRecordId,
			fieldSetConnectionId,
			groupConnectionId,
			projectKey,
		}: IssuePositionUpdaterArgs): boolean => {
			const contexts = searchViewContext?.contexts;
			let wasUpdated = false;

			if (!contexts?.length && parentIssueNodeRecordId) {
				// we know the parent issues id on create/update, but we need to find the edge to udpate the hasChildren field
				const parentIssueEdgeRecordId = Object.values(connections)
					.flatMap((connection) => {
						const { connectionId } = connection;
						const connectionRecord = store.get(connectionId);
						if (!connectionRecord) {
							return;
						}
						const { EDGES } = ConnectionInterface.get();
						const edges = connectionRecord.getLinkedRecords(EDGES);

						return edges;
					})
					.find((edge) => {
						const existingEdge =
							edge?.getLinkedRecord('node')?.getDataID() === parentIssueNodeRecordId;
						return existingEdge;
					})
					?.getDataID();
				if (parentIssueEdgeRecordId != null) {
					wasUpdated = updateParentHasChildren({
						store,
						parentIssueEdgeRecordId,
						connectionId: undefined,
						hasChildren: true,
						projectKey,
					});
				}
			}

			if (!contexts) {
				return wasUpdated;
			}

			Object.values(connections).forEach((connection) => {
				const { connectionId } = connection;
				const connectionRecord = store.get(connectionId);
				if (!connectionRecord) {
					return;
				}
				const { EDGES } = ConnectionInterface.get();
				const edges = connectionRecord.getLinkedRecords(EDGES);

				if (!edges) return false;

				const existingNodeIndex = edges.findIndex(
					(edge) => edge?.getLinkedRecord('node')?.getDataID() === issueRecordId,
				);

				const matchingContext = contexts.find((context) => {
					if (connection.type === 'PARENT_CHILDREN') {
						return context.parentIssueId === connection.parentId;
					}
					if (connection.type === 'GROUP_CHILDREN') {
						return context.jql === connection.jql;
					}
					if (connection.type === 'ROOT_ISSUES') {
						return context.parentIssueId == null && context.jql == null;
					}
					return false;
				});

				const relativeIssueIndex = edges.findIndex((edge) => {
					const issueId = edge?.getLinkedRecord('node')?.getValue('issueId');
					return (
						issueId === matchingContext?.afterIssueId || issueId === matchingContext?.beforeIssueId
					);
				});

				if (matchingContext) {
					// issue does not exist in the connection and issue to be positioned in reference to is not yet in view, so we let fetching of more issues position it
					if (edges.length > 0 && relativeIssueIndex === -1) {
						return;
					}

					// issue will exist on the connection
					if (existingNodeIndex === -1) {
						// issue is not previously on the connection, add it and update the owner if required
						addEdgeToConnection({
							store,
							connectionRecord,
							fieldSetsRecordId: fieldSetConnectionId,
							issueRecordId,
							position: matchingContext.position,
							projectKey,
						});
						updateConnectionDetails({
							store,
							connection,
							operation: 'add',
							netChangeInIssueCount: 1,
							remainingIssueCount: connectionRecord.getLinkedRecords('edges')?.length,
							groupConnectionId,
							projectKey,
						});

						wasUpdated = true;
						return;
					}
					if (existingNodeIndex === matchingContext.position) {
						// issue has not moved, noop
						return;
					}
					// issue has moved within the same connection
					ConnectionHandler.deleteNode(connectionRecord, issueRecordId);
					addEdgeToConnection({
						store,
						connectionRecord,
						fieldSetsRecordId: fieldSetConnectionId,
						issueRecordId,
						position: matchingContext.position,
						projectKey,
					});
					wasUpdated = true;
				} else if (existingNodeIndex !== -1) {
					// issue is no longer in the connection, remove it and update the owner if required
					ConnectionHandler.deleteNode(connectionRecord, issueRecordId);

					updateConnectionDetails({
						store,
						connection,
						operation: 'remove',
						netChangeInIssueCount: -1,
						remainingIssueCount: connectionRecord.getLinkedRecords('edges')?.length,
						groupConnectionId,
						projectKey,
					});
					wasUpdated = true;
				}
			});

			return wasUpdated;
		},
		[connections, updateConnectionDetails],
	);

	const issueDeleteUpdater = useCallback(
		({
			store,
			issueResourceId,
			groupConnectionId,
			projectKey,
		}: IssueDeleteUpdaterArgs): boolean => {
			const id = RelayDataID({ id: issueResourceId }, JIRA_ISSUE_TYPENAME);
			let wasDeleted = false;

			if (!id) {
				return wasDeleted;
			}

			Object.values(connections).forEach((connection) => {
				const connectionRecord = store.get(connection.connectionId);
				if (!connectionRecord) {
					return;
				}

				// check if the issue is in the connection
				if (
					connectionRecord
						.getLinkedRecords('edges')
						?.some((edge) => edge?.getLinkedRecord('node')?.getDataID() === id)
				) {
					ConnectionHandler.deleteNode(connectionRecord, id);

					updateConnectionDetails({
						store,
						connection,
						operation: 'remove',
						netChangeInIssueCount: -1,
						remainingIssueCount: connectionRecord.getLinkedRecords('edges')?.length,
						groupConnectionId,
						projectKey,
					});
					wasDeleted = true;
				}
			});

			return wasDeleted;
		},
		[connections, updateConnectionDetails],
	);

	const groupsForIssueUpdater = useCallback(
		({
			store,
			groupConnectionId,
			groupsForIssue,
		}: {
			store: RecordSourceProxy;
			groupConnectionId?: string;
			groupsForIssue: GroupDetails[];
		}): boolean => {
			let wasUpdated = false;

			if (!groupConnectionId) {
				return wasUpdated;
			}

			const groupsConnection = store.get(groupConnectionId);
			if (!groupsConnection) {
				return wasUpdated;
			}

			let groupEdges = groupsConnection.getLinkedRecords('edges') || [];
			const existingGroupIds = new Set(
				groupEdges?.map((edge) => edge?.getLinkedRecord('node')?.getValue('id')),
			);

			groupsForIssue.forEach((group) => {
				const newGroup = store.get(group.__id);
				if (!existingGroupIds.has(group.id) && newGroup) {
					groupEdges = insertNewGroupInGroupsConnection({
						store,
						groupsConnection,
						newGroup,
						groupEdges,
						afterGroupId: group.afterGroupId,
					});

					wasUpdated = true;
				}
			});

			groupsConnection.setLinkedRecords(groupEdges, 'edges');

			return wasUpdated;
		},
		[],
	);

	return {
		issuePositionUpdater,
		issueDeleteUpdater,
		groupsForIssueUpdater,
		updateIssuesOnConnection,
		updateNewParentsOnConnection,
	};
};
